import { useCallback, useState } from 'react';
import { supabase } from '@/lib/supabase/client';
import { UUID } from '@/types/common';
import { WorkOrderQC, WorkOrderWithDetails } from '@/types/workOrders';
import { WorkOrderBaseState } from './workOrderBase';
import { QCParameter } from '@/types/qc';

export interface CreateQCCheckRequest {
  work_order_id: string;
  operation_id: string;
  qc_point: string;
  inspector_name: string;
  result: 'Pass' | 'Fail' | 'Rework' | 'Pending';
  comments?: string;
  check_date: string;
  qc_template_id?: string;
  parameter_id?: string;
  measurement_value?: number;
  uom_id?: string;
}

export interface UpdateQCCheckRequest {
  result: 'Pass' | 'Fail' | 'Rework' | 'Pending';
  inspector_name: string;
  comments?: string;
  check_date: string;
  measurement_value?: number;
  uom_id?: string;
}

export interface QCTemplateWithParameters {
  template_id: string;
  template_code: string;
  template_name: string;
  parameters: QCParameter[];
}

export const useWorkOrderQC = (baseState: WorkOrderBaseState) => {
  const {
    loading,
    setLoading,
    selectedWorkOrder,
    setSelectedWorkOrder,
    toast
  } = baseState;
  
  const [qcTemplatesForOperation, setQCTemplatesForOperation] = useState<QCTemplateWithParameters[]>([]);

  /**
   * Fetch QC Checks for a Work Order with detailed information
   */
  const fetchQCChecks = useCallback(async (workOrderId: UUID) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_order_qc_with_details')
        .select('*')
        .eq('work_order_id', workOrderId)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error fetching QC checks',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Fetch QC Templates for a specific operation
   */
  const fetchQCTemplatesForOperation = useCallback(async (operationId: UUID) => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .rpc('get_operation_qc_templates', {
          operation_id: operationId
        });
      
      if (error) throw error;
      
      setQCTemplatesForOperation(data || []);
      return data || [];
    } catch (error: any) {
      toast({
        title: 'Error fetching QC templates',
        description: error.message,
        variant: 'destructive',
      });
      return [];
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Create a new QC check
   */
  const createQCCheck = useCallback(async (qcCheck: CreateQCCheckRequest): Promise<WorkOrderQC | null> => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_order_qc')
        .insert([qcCheck])
        .select()
        .single();
      
      if (error) throw error;
      
      // If this QC check is for the currently selected work order, refresh it
      if (selectedWorkOrder?.id === qcCheck.work_order_id) {
        const updatedQCChecks = await fetchQCChecks(qcCheck.work_order_id);
        if (updatedQCChecks && selectedWorkOrder) {
          setSelectedWorkOrder({
            ...selectedWorkOrder,
            qc_checks: updatedQCChecks
          } as WorkOrderWithDetails);
        }
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error creating QC check',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchQCChecks, setLoading, toast]);

  /**
   * Update an existing QC check
   */
  const updateQCCheck = useCallback(async (id: UUID, updates: UpdateQCCheckRequest): Promise<WorkOrderQC | null> => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('work_order_qc')
        .update(updates)
        .eq('id', id)
        .select()
        .single();
      
      if (error) throw error;
      
      // If this QC check is for the currently selected work order, refresh it
      if (selectedWorkOrder && data.work_order_id === selectedWorkOrder.id) {
        const updatedQCChecks = await fetchQCChecks(data.work_order_id);
        if (updatedQCChecks) {
          setSelectedWorkOrder({
            ...selectedWorkOrder,
            qc_checks: updatedQCChecks
          } as WorkOrderWithDetails);
        }
      }
      
      return data;
    } catch (error: any) {
      toast({
        title: 'Error updating QC check',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    } finally {
      setLoading(false);
    }
  }, [fetchQCChecks, setLoading, toast]);

  /**
   * Delete a QC check
   */
  const deleteQCCheck = useCallback(async (id: UUID): Promise<boolean> => {
    try {
      setLoading(true);
      
      // First get the work order ID so we can refresh after delete
      const { data: qcCheck, error: fetchError } = await supabase
        .from('work_order_qc')
        .select('work_order_id')
        .eq('id', id)
        .single();
      
      if (fetchError) throw fetchError;
      
      const { error } = await supabase
        .from('work_order_qc')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      // If this QC check is for the currently selected work order, refresh it
      if (selectedWorkOrder?.id === qcCheck.work_order_id) {
        const updatedQCChecks = await fetchQCChecks(qcCheck.work_order_id);
        if (updatedQCChecks) {
          setSelectedWorkOrder({
            ...selectedWorkOrder,
            qc_checks: updatedQCChecks
          } as WorkOrderWithDetails);
        }
      }
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error deleting QC check',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [fetchQCChecks, setLoading, toast]);

  /**
   * Prefill QC checks from templates
   */
  const prefillQCChecks = useCallback(async (jobCardId: UUID): Promise<boolean> => {
    try {
      setLoading(true);
      
      const { error } = await supabase
        .rpc('prefill_job_card_qc_checks', {
          job_card_id: jobCardId
        });
      
      if (error) throw error;
      
      toast({
        title: 'Success',
        description: 'QC checks from templates loaded successfully',
      });
      
      return true;
    } catch (error: any) {
      toast({
        title: 'Error prefilling QC checks',
        description: error.message,
        variant: 'destructive',
      });
      return false;
    } finally {
      setLoading(false);
    }
  }, [setLoading, toast]);

  /**
   * Auto-determine pass/fail status based on measured value and specs
   */
  const determineQCResult = useCallback((value: number, minSpec: number | null, maxSpec: number | null): 'Pass' | 'Fail' => {
    if (minSpec !== null && value < minSpec) {
      return 'Fail';
    }
    
    if (maxSpec !== null && value > maxSpec) {
      return 'Fail';
    }
    
    return 'Pass';
  }, []);

  return {
    loading,
    qcTemplatesForOperation,
    fetchQCChecks,
    fetchQCTemplatesForOperation,
    createQCCheck,
    updateQCCheck,
    deleteQCCheck,
    prefillQCChecks,
    determineQCResult
  };
};
